import { useEffect, useState } from "react";
import { AlertTriangle, Ban, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";
import { LinkedAccounts, blockPoints } from "@/components/FakeClustersAdmin";

export function SuspectsAdmin({ onBlocked }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/points/suspects").then(({ data }) => setList(data)).catch((e) => { setList([]); toast.error(formatErr(e)); }); }, []);
  const drop = (id) => setList((l) => l.filter((x) => x.user_id !== id));
  const block = async (u) => {
    try { await api.put(`/admin/users/${u.user_id}/exclusions`, { points: true }); drop(u.user_id); onBlocked({ ...u, no_points: true }); toast.success(`Punkte-Sammeln für ${u.name} gesperrt`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const ignore = async (u) => {
    try { await api.post(`/admin/points/suspects/${u.user_id}/ignore`); drop(u.user_id); toast.success("Für 7 Tage ausgeblendet"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="rounded-3xl bg-amber-50/60 border border-amber-200 p-5 space-y-4" data-testid="suspects-admin">
      <div>
        <h2 className="text-lg font-extrabold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Verdachts-Warnung</h2>
        <p className="text-sm text-slate-600">Mitglieder mit auffällig vielen Punkten in den letzten 24 Stunden oder in der letzten Stunde. Prüfe kurz und sperre bei Bedarf das Punkte-Sammeln.</p>
      </div>
      {!list ? <Loader2 className="w-5 h-5 animate-spin text-amber-500 mx-auto" /> : list.length === 0 ? <p className="text-sm text-slate-500 text-center py-3" data-testid="suspects-empty">Gerade nichts Auffälliges 👍</p> : (
        <ul className="space-y-3" data-testid="suspects-list">
          {list.map((u) => (
            <li key={u.user_id} className="rounded-2xl bg-white border border-amber-100 p-4 space-y-3" data-testid={`suspect-${u.user_id}`}>
              <div className="flex items-center gap-3">
                <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-9 h-9" noStory />
                <div className="flex-1 min-w-0"><p className="font-bold text-sm truncate">{u.name}</p><p className="text-xs text-slate-400 truncate">{u.email}</p></div>
                <span className="font-extrabold text-amber-600 tabular-nums" data-testid={`suspect-sum-${u.user_id}`}>+{u.sum.toLocaleString("de-DE")}</span>
              </div>
              <ul className="text-xs text-slate-600 space-y-0.5">{u.why.map((w) => <li key={w}>• {w}</li>)}</ul>
              <p className="text-xs text-slate-400">Meist durch: {u.top_kinds.join(", ")}</p>
              {u.linked?.length > 0 && (
                <div className="rounded-xl bg-rose-50/70 p-3 space-y-2" data-testid={`suspect-linked-${u.user_id}`}>
                  <p className="text-xs font-bold text-rose-600">{u.linked.length} verknüpfte {u.linked.length === 1 ? "Konto" : "Konten"} (Gerät/Netzwerk)</p>
                  <LinkedAccounts accounts={u.linked} testId={`suspect-linked-list-${u.user_id}`} onBlock={async (accs) => {
                    try { await blockPoints(accs[0].user_id); onBlocked({ ...accs[0], no_points: true }); setList((l) => l.map((x) => (x.user_id === u.user_id ? { ...x, linked: x.linked.map((a) => (a.user_id === accs[0].user_id ? { ...a, no_points: true } : a)) } : x))); toast.success(`Punkte-Sammeln für ${accs[0].name} gesperrt`); }
                    catch (e) { toast.error(formatErr(e)); }
                  }} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => ignore(u)} data-testid={`suspect-ignore-${u.user_id}`} className="h-10 rounded-full border-2 border-slate-200 text-slate-600 text-sm font-bold inline-flex items-center justify-center gap-1.5"><EyeOff className="w-4 h-4" /> Ignorieren</button>
                <button onClick={() => block(u)} data-testid={`suspect-block-${u.user_id}`} className="h-10 rounded-full bg-rose-600 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Ban className="w-4 h-4" /> Sperren</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
